import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, RefreshCw, Users, UserPlus, CheckCircle, MapPin,
  TrendingUp, DollarSign, Wrench, Building2, Home, Zap
} from "lucide-react";
import { BACKEND_URL, PARTNER_ROLE_CFG } from "./networkConstants";

const ROLE_ICON_MAP = {
  investor:   TrendingUp,
  wholesaler: Home,
  lender:     DollarSign,
  contractor: Wrench,
  realtor:    Building2,
  default:    Users,
};

const ROLE_FILTERS = [
  { key: "all",        label: "All"          },
  { key: "investor",   label: "Investors"    },
  { key: "lender",     label: "Lenders"      },
  { key: "contractor", label: "Contractors"  },
  { key: "realtor",    label: "Realtors"     },
];

function PartnerCard({ partner, onConnect }) {
  const [imgErr, setImgErr] = useState(false);
  const roleCfg    = PARTNER_ROLE_CFG[partner.role] || PARTNER_ROLE_CFG.default;
  const RoleIcon   = ROLE_ICON_MAP[partner.role] || ROLE_ICON_MAP.default;
  const initial    = (partner.name || "A")[0].toUpperCase();
  const memberSince = partner.created_at ? new Date(partner.created_at).getFullYear() : null;

  const stats = [];
  if (partner.capital_range)           stats.push({ label: "Max Capital", value: partner.capital_range,      color: "text-emerald-400" });
  if (partner.rate)                    stats.push({ label: "Rate",        value: `${partner.rate}%`,         color: "text-[#D4AF37]"  });
  if (partner.ltv)                     stats.push({ label: "LTV",         value: `${partner.ltv}%`,          color: "text-blue-400"   });
  if ((partner.deal_count   || 0) > 0) stats.push({ label: "Deals",       value: partner.deal_count,         color: "text-white/70"   });
  if ((partner.closed_count || 0) > 0) stats.push({ label: "Closed",      value: partner.closed_count,       color: "text-[#00FF88]"  });
  if (partner.specialties?.length)     stats.push({ label: "Specialty",   value: partner.specialties[0],     color: "text-orange-400" });

  const actionLabel = partner.role === "lender"     ? "Request Funding"
                    : partner.role === "contractor" ? "Request Quote"
                    : partner.role === "realtor"    ? "Request Showing"
                    : "Message";

  return (
    <div data-testid={`partner-${partner.user_id}`}
      className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 hover:shadow-lg hover:shadow-[#D4AF37]/[0.03] transition-all duration-300 group">
      <div className={`h-0.5 ${partner.is_verified ? 'bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]/20' : 'bg-white/[0.03]'}`} />
      <div className="p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="relative flex-shrink-0">
            {partner.profile_picture_url && !imgErr ? (
              <img src={partner.profile_picture_url.startsWith("http") ? partner.profile_picture_url : `${process.env.REACT_APP_BACKEND_URL}${partner.profile_picture_url}`}
                alt={partner.name} onError={() => setImgErr(true)}
                className={`w-14 h-14 rounded-xl object-cover border-2 ${roleCfg.border}`} />
            ) : (
              <div className={`w-14 h-14 rounded-xl ${roleCfg.bg} border-2 ${roleCfg.border} flex items-center justify-center font-bold text-xl ${roleCfg.color}`}>
                {initial}
              </div>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#00FF88] border-2 border-[#0D0D0D]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-bold text-sm truncate">{partner.name || "Partner"}</span>
              {partner.is_verified && (
                <span className="flex items-center gap-0.5 text-[9px] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-1.5 py-0.5 rounded-full font-bold">
                  <CheckCircle className="w-2.5 h-2.5" /> Verified
                </span>
              )}
            </div>
            <div className={`inline-flex items-center gap-1 text-[10px] mt-0.5 px-2 py-0.5 rounded-full ${roleCfg.bg} ${roleCfg.color} border ${roleCfg.border}`}>
              <RoleIcon className="w-2.5 h-2.5" /> {roleCfg.label}
            </div>
            {partner.organization_name && partner.organization_name !== partner.name && (
              <p className="text-white/30 text-[11px] mt-0.5 truncate">{partner.organization_name}</p>
            )}
          </div>
          {memberSince && <span className="text-[9px] text-white/20 flex-shrink-0">{memberSince}</span>}
        </div>

        {partner.bio && (
          <p className="text-white/40 text-xs leading-relaxed line-clamp-2 mb-4">{partner.bio}</p>
        )}

        {stats.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {stats.slice(0, 3).map((s, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-2.5 py-1.5 text-center min-w-[64px]">
                <p className={`text-xs font-bold ${s.color}`}>{s.value}</p>
                <p className="text-[9px] text-white/25">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {partner.location && (
          <div className="flex items-center gap-1.5 text-white/30 text-xs mb-4">
            <MapPin className="w-3 h-3 text-[#D4AF37]/40" /> {partner.location}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={() => onConnect(partner.user_id)} data-testid={`connect-btn-${partner.user_id}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs border ${roleCfg.border} ${roleCfg.color} rounded-xl hover:${roleCfg.bg} transition-all font-medium`}>
            <UserPlus className="w-3 h-3" /> Connect
          </button>
          <button data-testid={`action-btn-${partner.user_id}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 text-[#D4AF37] rounded-xl hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/40 transition-all font-bold">
            <Zap className="w-3 h-3" /> {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  useEffect(() => { fetchPartners(); }, [roleFilter]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/network/users`, {
        params: { role: roleFilter !== "all" ? roleFilter : undefined }, withCredentials: true
      });
      setPartners(res.data || []);
    } catch { }
    finally { setLoading(false); }
  };

  const connect = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/network/connect`, { target_user_id: userId }, { withCredentials: true });
      toast.success("Connection request sent!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to connect"); }
  };

  const filtered = search
    ? partners.filter(p =>
        (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.organization_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.location || "").toLowerCase().includes(search.toLowerCase())
      )
    : partners;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-white font-bold text-base">Partner Network</p>
          <p className="text-white/30 text-xs mt-0.5">{filtered.length} professionals ready to collaborate</p>
        </div>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, org, or location…"
          className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/30" />
      </div>
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-none flex-nowrap">
        {ROLE_FILTERS.map(r => {
          const cfg = PARTNER_ROLE_CFG[r.key] || PARTNER_ROLE_CFG.default;
          return (
            <button key={r.key} onClick={() => setRoleFilter(r.key)}
              className={`text-xs px-4 py-1.5 rounded-full border whitespace-nowrap transition-all font-medium flex-shrink-0 ${
                roleFilter === r.key ? `${cfg.bg} ${cfg.border} ${cfg.color}` : 'border-white/[0.06] text-white/40 hover:text-white'
              }`}>
              {r.label}
            </button>
          );
        })}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 text-white/20 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#0A0A0A] border border-dashed border-white/[0.06] rounded-2xl">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-white/15" />
          </div>
          <p className="text-white/40 text-sm font-medium">No partners found</p>
          <p className="text-white/20 text-xs mt-1">Try a different filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
          {filtered.map(partner => <PartnerCard key={partner.user_id} partner={partner} onConnect={connect} />)}
        </div>
      )}
    </div>
  );
}
